import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { employeesAPI } from "../../services/api";
import { toast } from "react-toastify";

const ROLE_OPTIONS = [
  { value: 'ATS', label: 'ATS' },
  { value: 'CRM', label: 'CRM' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'USER_MANAGEMENT', label: 'User Management' },
];

const CreateUserModal = ({ onClose, onCreate }) => {
  const [auth, setAuth] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    roles: ["ATS"],
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  /* ===============================
     SESSION INIT
     =============================== */
  useEffect(() => {
    try {
      const profileRaw = localStorage.getItem("userProfile");
      const token = localStorage.getItem("token");
      if (!profileRaw || !token) throw new Error("Missing session");

      const profile = JSON.parse(profileRaw);
      const organizationId =
        profile?.organizationId || localStorage.getItem("organizationId");
      const email = profile?.email;

      if (!organizationId || !email) throw new Error("Invalid profile");

      setAuth({ token, organizationId, createdBy: email, role: profile.role });
      setSessionChecked(true);
    } catch {
      toast.error("Session expired. Please login again.");
      onClose();
    }
  }, [onClose]);

  if (!sessionChecked) return null;

  /* ===============================
     VALIDATION RULES
     =============================== */
  const validateField = (name, value) => {
    switch (name) {

      case "name": {
        if (!value.trim())
          return "Full name is required.";
        if (value.trim().length < 2)
          return "Name must be at least 2 characters.";
        if (value.trim().length > 60)
          return "Name must not exceed 60 characters.";
        if (!/^[a-zA-Z\s'-]+$/.test(value.trim()))
          return "Name can only contain letters, spaces, hyphens, or apostrophes.";
        return "";
      }

      case "email": {
        if (!value.trim())
          return "Email address is required.";
        // Must have exactly one @
        if ((value.match(/@/g) || []).length !== 1)
          return "Email must contain exactly one '@' symbol.";
        // Standard RFC-ish email regex
        if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(value.trim()))
          return "Enter a valid email address (e.g. name@domain.com).";
        if (value.trim().length > 254)
          return "Email address is too long.";
        return "";
      }

      case "phoneNumber": {
        if (!value.trim())
          return "Phone number is required.";
        // Strip spaces/dashes for digit-only check
        const digitsOnly = value.replace(/[\s\-]/g, "");
        let normalized = value.replace(/\D/g, "");
        if (normalized.length === 12 && normalized.startsWith("91")) normalized = normalized.slice(2);
        if (normalized.length === 11 && normalized.startsWith("0")) normalized = normalized.slice(1);
        if (!/^\d+$/.test(normalized))
          return "Phone number must contain digits only — no letters or special characters.";
        if (normalized.length !== 10)
          return "Phone number must be exactly 10 digits.";
        if (!/^[6-9]/.test(normalized))
          return "Phone number must start with 6, 7, 8, or 9.";
        return "";
      }

      default:
        return "";
    }
  };

  const validateAll = () => {
    const newErrors = {
      name:        validateField("name",        formData.name),
      email:       validateField("email",       formData.email),
      phoneNumber: validateField("phoneNumber", formData.phoneNumber),
      roles:       formData.roles.length === 0 ? "At least one role must be selected." : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every(e => e === "");
  };

  /* ===============================
     HANDLERS
     =============================== */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Phone: normalize (strip country/trunk prefix) then clamp to 10 digits
    if (name === "phoneNumber") {
      let normalized = value.replace(/[^\d]/g, "");
      if (normalized.length === 12 && normalized.startsWith("91")) normalized = normalized.slice(2);
      if (normalized.length === 11 && normalized.startsWith("0")) normalized = normalized.slice(1);
      const cleaned = normalized.slice(0, 10);
      setFormData(p => ({ ...p, phoneNumber: cleaned }));
      if (touched.phoneNumber)
        setErrors(p => ({ ...p, phoneNumber: validateField("phoneNumber", cleaned) }));
      return;
    }

    setFormData(p => ({ ...p, [name]: value }));
    if (touched[name])
      setErrors(p => ({ ...p, [name]: validateField(name, value) }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(p => ({ ...p, [name]: true }));
    setErrors(p => ({ ...p, [name]: validateField(name, value) }));
  };

  const handleRoleToggle = (value) => {
    setFormData(prev => {
      const roles = prev.roles.includes(value)
        ? prev.roles.filter(r => r !== value)
        : [...prev.roles, value];
      setErrors(p => ({
        ...p,
        roles: roles.length === 0 ? "At least one role must be selected." : "",
      }));
      return { ...prev, roles };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Mark all fields touched so errors show
    setTouched({ name: true, email: true, phoneNumber: true });

    if (!validateAll()) {
      toast.warning("Please fix the highlighted errors before submitting.");
      setTimeout(() => {
        const el = document.querySelector('[data-error="true"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name:           formData.name.trim(),
        email:          formData.email.trim().toLowerCase(),
        phoneNumber:    formData.phoneNumber.trim(),
        roles:          formData.roles,
        organizationId: auth.organizationId,
        createdBy:      auth.createdBy,
      };

      const { data } = await employeesAPI.createEmployee(payload);

      toast.success(
        data?.password
          ? `Employee created! Temporary Password: ${data.password}`
          : "Employee created successfully",
        { autoClose: 8000 }
      );
      onCreate?.();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (err.response?.status === 409 ? "Email already exists." : "Failed to create employee.")
      );
    } finally {
      setLoading(false);
    }
  };

  /* ===============================
     FIELD UI HELPER
     =============================== */
  const fieldClass = (name) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-colors ${
      errors[name] && touched[name]
        ? "border-red-400 bg-red-50"
        : "border-gray-300"
    }`;

  const ErrorMsg = ({ field }) =>
    errors[field] && touched[field] ? (
      <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
        <span>⚠</span> {errors[field]}
      </p>
    ) : null;

  /* ===============================
     UI
     =============================== */
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md h-full shadow-xl animate-slideInRight flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Add New User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate
          className="flex-1 overflow-y-auto px-6 py-6 space-y-5">

          {/* Full Name */}
          <div data-error={!!(errors.name && touched.name)}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              name="name"
              placeholder="e.g. Ravi Kumar"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={60}
              className={fieldClass("name")}
            />
            <ErrorMsg field="name" />
          </div>

          {/* Email */}
          <div data-error={!!(errors.email && touched.email)}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              name="email"
              type="text"           // kept as text so we control all validation
              placeholder="e.g. ravi@company.com"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={254}
              className={fieldClass("email")}
            />
            <ErrorMsg field="email" />
          </div>

          {/* Phone */}
          <div data-error={!!(errors.phoneNumber && touched.phoneNumber)}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 select-none">
                +91
              </span>
              <input
                name="phoneNumber"
                type="tel"
                inputMode="numeric"
                placeholder="9XXXXXXXXX"
                value={formData.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={10}
                className={`${fieldClass("phoneNumber")} pl-12`}
              />
            </div>
            {/* Live digit counter */}
            <p className={`text-xs mt-1 text-right ${
              formData.phoneNumber.length === 10 ? "text-green-600" : "text-gray-400"
            }`}>
              {formData.phoneNumber.length}/10
            </p>
            <ErrorMsg field="phoneNumber" />
          </div>

          {/* Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Roles <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {ROLE_OPTIONS.map(opt => (
                <label key={opt.value}
                  className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={formData.roles.includes(opt.value)}
                    onChange={() => handleRoleToggle(opt.value)}
                    className="w-4 h-4 rounded border-gray-300 accent-[#1C4E6D]"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
            {errors.roles && (
              <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                <span>⚠</span> {errors.roles}
              </p>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-white rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ backgroundColor: "#1C4E6D" }}
              onMouseEnter={e => !loading && (e.currentTarget.style.backgroundColor = "#153b52")}
              onMouseLeave={e => !loading && (e.currentTarget.style.backgroundColor = "#1C4E6D")}
            >
              {loading ? "Creating..." : "Create Employee"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;